import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_MONITORINGISSUE_LOAD_BY_ID,
    ACTION_MONITORINGISSUES_LOAD_BY_MONITORING_ID,
    ACTION_MONITORINGISSUE_LOAD_FAILURE,
    ACTION_MONITORINGISSUE_LOAD_SUCCESS,

    ACTION_MONITORINGISSUE_SAVE,
    ACTION_MONITORINGISSUE_SAVE_SUCCESS,
    ACTION_MONITORINGISSUE_SAVE_FAILURE,

    ACTION_MONITORINGISSUE_CREATE,
    ACTION_MONITORINGISSUE_CREATE_SUCCESS,
    ACTION_MONITORINGISSUE_CREATE_FAILURE,

    ACTION_MONITORINGISSUE_VALIDATE,
    ACTION_MONITORINGISSUE_VALIDATE_SUCCESS,
    ACTION_MONITORINGISSUE_VALIDATE_FAILURE,

    ACTION_MONITORINGISSUE_CLEAR,
    ACTION_MONITORINGISSUE_FILTER,

    MonitoringIssueAction,
} from '../actions/monitoringIssue';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IMonitoringIssueState {
    formData: Dtos.MonitoringIssue | Dtos.MonitoringIssue[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.MonitoringIssue[];
    formLength: number;
}

const initialState: IMonitoringIssueState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IMonitoringIssueState, _isLoading: boolean): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IMonitoringIssueState,
    formData: Dtos.MonitoringIssue | Dtos.MonitoringIssue[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IMonitoringIssueState, responseStatus: Dtos.ResponseStatus): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IMonitoringIssueState, formFilter: IFormFilter): IMonitoringIssueState {

    const items: Dtos.MonitoringIssue[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" ||
            p.patient.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IMonitoringIssueState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IMonitoringIssueState, propertiesToClear?: string[]): IMonitoringIssueState {

    if (!propertiesToClear) {
        const newState: IMonitoringIssueState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IMonitoringIssueState, _isSaving: boolean): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IMonitoringIssueState,
    formData: Dtos.MonitoringIssue | Dtos.MonitoringIssue[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IMonitoringIssueState, responseStatus: Dtos.ResponseStatus): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IMonitoringIssueState, _isCreating: boolean): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IMonitoringIssueState,
    formData: Dtos.MonitoringIssue,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IMonitoringIssueState, responseStatus: Dtos.ResponseStatus): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IMonitoringIssueState, _isValidating: boolean): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IMonitoringIssueState,
    formData: Dtos.MonitoringIssue,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IMonitoringIssueState, responseStatus: Dtos.ResponseStatus): IMonitoringIssueState {
    const newState: IMonitoringIssueState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}


const MonitoringIssueReducer: Reducer<IMonitoringIssueState> = (state: IMonitoringIssueState = initialState, action: MonitoringIssueAction) => {
    switch (action.type) {
        case ACTION_MONITORINGISSUE_LOAD_BY_ID:
        case ACTION_MONITORINGISSUES_LOAD_BY_MONITORING_ID:
            return loading(state, true);
        case ACTION_MONITORINGISSUE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_MONITORINGISSUE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_MONITORINGISSUE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_MONITORINGISSUE_SAVE:
            return saving(state, true);
        case ACTION_MONITORINGISSUE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_MONITORINGISSUE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_MONITORINGISSUE_CREATE:
            return creating(state, true);
        case ACTION_MONITORINGISSUE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_MONITORINGISSUE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_MONITORINGISSUE_VALIDATE:
            return validating(state, true);
        case ACTION_MONITORINGISSUE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_MONITORINGISSUE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_MONITORINGISSUE_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default MonitoringIssueReducer;